import type React from "react";
import { useState } from "react";

import { faMessage } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import { usePutTherapistMutation } from "api/hooks/usePutTherapistMutation";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";

import { ButtonWrapper, StyledTextArea } from "../shared/styles";

interface Props {
  customWelcomeMessageEnabled: boolean | false;
}

export const WelcomeMessage: React.FC<Props> = ({ customWelcomeMessageEnabled }) => {
  const { t } = useTranslation();
  const { profile, setProfile } = useProfileContext();
  const [isEditing, setIsEditing] = useState(false);
  const putTherapistMutation = usePutTherapistMutation();
  const { handleSubmit, register, reset } = useForm({
    defaultValues: {
      welcome_message_extra: profile?.therapist_profile.welcome_message_extra || "",
    },
  });

  const handleChangeCustomWelcomeMessageClick = () => {
    putTherapistMutation.reset();
    setIsEditing(!isEditing);
  };

  const onSubmit = async ({ welcome_message_extra }: { welcome_message_extra: string }) => {
    invariant(profile);

    const therapistId = profile.id;
    const data = {
      therapist_profile: {
        welcome_message_extra,
      },
    };

    const variables = { data, therapistId };

    putTherapistMutation.mutate(variables, {
      onSuccess(response) {
        setProfile(response);
        reset({
          welcome_message_extra: data.therapist_profile.welcome_message_extra || "",
        });
        setIsEditing(!isEditing);
      },
    });
  };

  const GeneratedWelcomeMessage = () => {
    return (
      <Stack spacing={1}>
        <Typography variant="body_thin">{t("settings.welcome_message.paragraph_1")}</Typography>
        <Typography variant="body_thin">{t("settings.welcome_message.paragraph_2")}</Typography>
        <Typography variant="body_thin">{t("settings.welcome_message.paragraph_3")}</Typography>
        <Typography variant="body_thin">{t("settings.welcome_message.paragraph_4")}</Typography>
      </Stack>
    );
  };

  const GeneratedGreeting = () => {
    return (
      <>
        <Typography variant="body_thin">{t("settings.welcome_message.greeting")}</Typography>
        <Typography variant="body_thin">{`${profile?.first_name ?? ""} ${profile?.last_name ?? ""}`}</Typography>
      </>
    );
  };

  return (
    <WidgetBox
      data-testid="welcome-message"
      icon={<FontAwesomeIcon icon={faMessage} />}
      title={t("settings.welcome_message.heading")}
      description={t("settings.welcome_message.subheading")}
      color="blue"
      toolbar={
        customWelcomeMessageEnabled && !isEditing ? (
          <BaseButton
            onClick={handleChangeCustomWelcomeMessageClick}
            dataTestID="edit-welcome-message-button"
            variant="action"
            icon="pen"
            text={t("settings.profile_information.change_profile_information")}
            size="large"
          />
        ) : undefined
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} data-testid="welcome-message">
        {isEditing ? (
          <>
            <GeneratedWelcomeMessage />
            <StyledTextArea
              {...register("welcome_message_extra")}
              rows={7}
              placeholder={t("settings.welcome_message.custom_welcome_message")}
            />
            <GeneratedGreeting />
            <ButtonWrapper>
              <BaseButton
                type="button"
                onClick={handleChangeCustomWelcomeMessageClick}
                text={t("buttons.cancel")}
                variant="secondary"
                size="large"
              />
              <BaseButton type="submit" text={t("buttons.save")} size="large" />
            </ButtonWrapper>
          </>
        ) : (
          <>
            <Stack spacing={2} mb={2}>
              <GeneratedWelcomeMessage />
              <Typography variant="body_thin" data-testid="custom-welcome-message">
                {profile?.therapist_profile?.welcome_message_extra}
              </Typography>
            </Stack>
            <GeneratedGreeting />
          </>
        )}
        {putTherapistMutation.isError && (
          <Notification variant="danger" style={{ margin: "10px 0" }}>
            {t("errors.generic")}
          </Notification>
        )}
      </form>
    </WidgetBox>
  );
};
